import useModal from '@area2k/use-modal';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import InviteAdminModal from './InviteAdminModal';

import Avatar from '@/components/Avatar';
import Badge from '@/components/Badge';
import Card from '@/components/Card';
import EmptyState from '@/components/EmptyState';
import LoadingState from '@/components/LoadingState';
import Pagination from '@/components/Pagination';
import ResourceList from '@/components/ResourceList';
import Stack from '@/components/Stack';
import TextStack from '@/components/TextStack';
import { Body, Small } from '@/components/Typography';
import { TIME_TO_REFRESH } from '@/constants/general';
import useAuth from '@/hooks/useAuth';
import useMediaQuery from '@/hooks/useMediaQuery';
import ROUTES from '@/routes/routes';
import { GetCustomerQuery } from '@/types/graphql';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  customer: GetCustomerQuery['customer'];
  currentAdminIsCustomerAdmin: boolean;
  accordion?: boolean;
};

type FilterOption = 'all' | 'active' | 'pending' | 'disabled';

const FILTER_LABELS: Record<FilterOption, string> = {
  all: 'All',
  active: 'Active',
  pending: 'Pending',
  disabled: 'Disabled',
};

type AdminUser = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string | null;
  active: boolean;
};

type CustomerAdminItem = {
  id: number;
  active: number;
  accepted_at: string | null;
  avatar_url: string | null;
  user: AdminUser;
};

type AdminsResponse = {
  success: boolean;
  data: CustomerAdminItem[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
};

const AdminList = ({
  customer,
  currentAdminIsCustomerAdmin,
  accordion = false,
}: Props) => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<FilterOption>('all');
  const [admins, setAdmins] = useState<CustomerAdminItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const { currentAdmin } = useAuth();

  const handleFilterChange = (newFilter: FilterOption) => {
    setFilter(newFilter);
    setPage(1);
  };

  // Map frontend filter to REST status param
  const statusParam = useMemo(() => {
    if (filter === 'active') return 'enabled';
    if (filter === 'pending') return 'pending';
    if (filter === 'disabled') return 'disabled';
    return null;
  }, [filter]);

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '10',
      });
      if (statusParam) params.append('status', statusParam);

      const response = await axiosRubyClient.get<AdminsResponse>(
        `/api/admin/customers/${customer.id}/admins?${params.toString()}`,
      );

      if (response.data.success) {
        setAdmins(response.data.data);
        setTotalPages(response.data.pagination.total_pages);
      }
    } catch (err) {
      console.error('Error fetching customer admins:', err);
    } finally {
      setLoading(false);
    }
  }, [customer.id, page, statusParam]);

  useEffect(() => {
    fetchAdmins();
    const interval = setInterval(fetchAdmins, TIME_TO_REFRESH);
    return () => clearInterval(interval);
  }, [fetchAdmins]);

  const [showinviteEmployeeModal, hideinviteEmployeeModal] = useModal(
    () => (
      <InviteAdminModal
        customer={customer}
        hideModal={() => {
          hideinviteEmployeeModal();
          setFilter('pending');
          setPage(1);
        }}
      />
    ),
    [customer],
  );

  const filterActions = (Object.keys(FILTER_LABELS) as FilterOption[]).map(
    (key) => ({
      a11yLabel: `Show ${FILTER_LABELS[key]} administrators`,
      label: filter === key ? `${FILTER_LABELS[key]} ✓` : FILTER_LABELS[key],
      onAction: () => handleFilterChange(key),
    }),
  );

  const renderItem = (item: CustomerAdminItem) => {
    const isCurrentCustomerAdmin =
      currentAdminIsCustomerAdmin && currentAdmin?.id === item.id;

    const itemHref = () => {
      if (!currentAdminIsCustomerAdmin) {
        return `admin/${item.id}`;
      } else if (isCurrentCustomerAdmin) {
        return `../${ROUTES.myProfile}`;
      }
      return `../${ROUTES.myTeam}/${item.id}`;
    };

    const badgeLabel = () => {
      if (item.user.active) {
        if (item.accepted_at) {
          return 'Verified';
        } else {
          return 'Pending';
        }
      }
      return item.active ? 'Disabled' : 'Deleted';
    };

    const badgeStatus = () => {
      if (item.user.active) {
        if (item.accepted_at) {
          return 'theme';
        } else {
          return 'warning';
        }
      }
      return 'danger';
    };

    const isActive = !!item.active;

    return (
      <ResourceList.Item
        key={`customer-admin-item-${item.id}`}
        disabled={!isActive}
        media={
          <Avatar
            firstName={item.user.first_name}
            size="sm"
            src={item.avatar_url}
          />
        }
        to={isActive ? itemHref() : ''}
      >
        <Stack wrap={phoneOnly}>
          <TextStack>
            <Body>
              {item.user.first_name} {item.user.last_name}
            </Body>
            <Small>{item.user.email}</Small>
          </TextStack>
          <Badge label={badgeLabel()} status={badgeStatus()} />
        </Stack>
      </ResourceList.Item>
    );
  };

  if (loading && admins.length === 0) {
    return (
      <Card title="Administrators">
        <Card.Section>
          <LoadingState overlayColor="white" />
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card
      accordion={accordion}
      actions={[
        {
          a11yLabel: 'Filter administrators',
          label: 'Filter',
          icon: faFilter,
          actions: [filterActions],
        },
        {
          a11yLabel: 'Invite a new administrator',
          label: 'Invite',
          onAction: showinviteEmployeeModal,
          id: 'invite-admin',
        },
      ]}
      title="Administrators"
    >
      <ResourceList
        emptyState={
          <EmptyState
            text="Invite a new administrator by clicking the button above"
            title="No administrators"
          />
        }
        items={admins}
        renderItem={renderItem}
      />
      {admins.length > 0 && totalPages > 1 && (
        <Card.Section>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </Card.Section>
      )}
    </Card>
  );
};

export default AdminList;
